/**
 * html-to-image copia o estilo computado COMPLETO (centenas de propriedades) em cada
 * elemento do SVG exportado, o que gera arquivos de vários MB. Como o SVG exportado é
 * autônomo (sem folhas de estilo), declarações iguais ao padrão do navegador para aquela
 * tag são redundantes e podem ser removidas sem alterar a renderização.
 */

const SVG_PREFIX = 'data:image/svg+xml;charset=utf-8,'

type DefaultsCache = Map<string, Map<string, string>>

// Propriedades herdadas: se o valor é igual ao do pai, o filho herda sozinho.
const INHERITED = new Set([
  'color', 'cursor', 'direction', 'visibility', 'pointer-events', 'line-height', 'letter-spacing',
  'word-spacing', 'white-space', 'white-space-collapse', 'text-wrap-mode', 'text-wrap-style',
  'text-align', 'text-indent', 'text-transform', 'text-shadow', 'text-rendering', 'tab-size',
  'caret-color', 'scrollbar-color', 'list-style', 'quotes', 'user-select', 'fill', 'stroke',
  '-webkit-tap-highlight-color', '-webkit-text-fill-color', '-webkit-text-stroke-color',
  '-webkit-font-smoothing',
])
// Controles de formulário não herdam fonte/cor do pai por padrão (o UA define as suas).
const FORM_CONTROLS = new Set(['button', 'input', 'select', 'textarea', 'optgroup'])
// Valores computados que dependem do contexto (min-* 'auto' em flex/grid; origens em px).
const isContextDependent = (prop: string) =>
  prop === 'min-width' || prop === 'min-height' || prop === 'transform-origin' || prop === 'perspective-origin'
const isInherited = (prop: string) => INHERITED.has(prop) || prop.startsWith('font-')

// Equivalentes lógicos (block/inline) das propriedades físicas: o SVG já traz as físicas.
const isLogical = (prop: string) =>
  /(^|-)(block|inline)(-|$)/.test(prop) && prop !== 'display'

// Prefixos cujo efeito depende de um "style"/"width" já removido por ser padrão.
const DEPENDENT_GROUPS: Array<{ gate: string; whenEquals: string; drop: RegExp }> = [
  { gate: 'outline-style', whenEquals: 'none', drop: /^outline-/ },
  { gate: 'column-rule-style', whenEquals: 'none', drop: /^(column|row)-rule-/ },
  { gate: 'border-top-width', whenEquals: '0px', drop: /^border-top-(style|color)$/ },
  { gate: 'border-right-width', whenEquals: '0px', drop: /^border-right-(style|color)$/ },
  { gate: 'border-bottom-width', whenEquals: '0px', drop: /^border-bottom-(style|color)$/ },
  { gate: 'border-left-width', whenEquals: '0px', drop: /^border-left-(style|color)$/ },
]

function readDefaults(
  sandbox: Document,
  host: Element,
  element: Element,
  cache: DefaultsCache,
): Map<string, string> {
  const key = `${element.namespaceURI}|${element.localName}`
  const cached = cache.get(key)
  if (cached) return cached

  const probe = sandbox.createElementNS(element.namespaceURI, element.localName)
  host.appendChild(probe)
  const computed = sandbox.defaultView!.getComputedStyle(probe)
  const defaults = new Map<string, string>()
  for (let i = 0; i < computed.length; i += 1) {
    const prop = computed[i]
    defaults.set(prop, computed.getPropertyValue(prop))
  }
  host.removeChild(probe)
  cache.set(key, defaults)
  return defaults
}

function readStyle(element: Element): Map<string, string> {
  const map = new Map<string, string>()
  const style = (element as HTMLElement).style
  if (!style) return map
  for (let i = 0; i < style.length; i += 1) {
    const prop = style[i]
    const priority = style.getPropertyPriority(prop)
    map.set(prop, `${style.getPropertyValue(prop)}${priority ? ` !${priority}` : ''}`)
  }
  return map
}

function stripDefaultStyles(root: Element, sandbox: Document, host: Element): void {
  const cache: DefaultsCache = new Map()
  const elements = [root, ...Array.from(root.querySelectorAll('*'))]
  // Valores originais (antes da limpeza) para comparar filho com pai.
  const originals = new Map<Element, Map<string, string>>()
  for (const element of elements) originals.set(element, readStyle(element))

  for (const element of elements) {
    const own = originals.get(element)!
    if (own.size === 0) continue

    const defaults = readDefaults(sandbox, host, element, cache)
    const parent = element.parentElement ? originals.get(element.parentElement) : undefined
    const kept = new Map<string, string>()

    for (const [prop, value] of own) {
      // Variáveis CSS já vêm resolvidas nos valores computados.
      if (prop.startsWith('--')) continue
      if (isLogical(prop)) continue
      // Atributo de apresentação SVG (fill, stroke-dasharray, font-family...) perde para o
      // style inline; sem o style, o atributo passaria a valer e mudaria o resultado.
      if (element.hasAttribute(prop)) {
        kept.set(prop, value)
        continue
      }
      if (isInherited(prop)) {
        // Herdada: o valor padrão só vale se não houver pai com estilo; do contrário só
        // dá para omitir quando é idêntico ao do pai.
        const useParent = parent && !FORM_CONTROLS.has(element.localName)
        const inherited = useParent ? parent.get(prop) : defaults.get(prop)
        if (inherited === value) continue
      } else if (!isContextDependent(prop) && defaults.get(prop) === value) {
        continue
      }
      kept.set(prop, value)
    }

    for (const group of DEPENDENT_GROUPS) {
      if ((own.get(group.gate) ?? defaults.get(group.gate)) !== group.whenEquals) continue
      for (const prop of Array.from(kept.keys())) {
        if (group.drop.test(prop)) kept.delete(prop)
      }
    }

    if (kept.size === 0) element.removeAttribute('style')
    else element.setAttribute('style', Array.from(kept, ([prop, value]) => `${prop}: ${value}`).join('; '))
  }
}

/** Reduz o tamanho de um data URL de SVG gerado por `toSvg`; devolve o original se falhar. */
export function slimSvgDataUrl(dataUrl: string): string {
  if (!dataUrl.startsWith(SVG_PREFIX) || typeof document === 'undefined') return dataUrl

  const iframe = document.createElement('iframe')
  iframe.setAttribute('aria-hidden', 'true')
  iframe.style.cssText = 'position:fixed;left:-99999px;top:0;width:0;height:0;border:0;visibility:hidden'
  document.body.appendChild(iframe)

  try {
    const sandbox = iframe.contentDocument
    if (!sandbox?.body) return dataUrl

    const markup = decodeURIComponent(dataUrl.slice(SVG_PREFIX.length))
    const parsed = new DOMParser().parseFromString(markup, 'image/svg+xml')
    if (parsed.querySelector('parsererror')) return dataUrl

    const foreign = parsed.querySelector('foreignObject')
    if (!foreign) return dataUrl

    stripDefaultStyles(foreign, sandbox, sandbox.body)

    const slim = new XMLSerializer().serializeToString(parsed.documentElement)
    return `${SVG_PREFIX}${encodeURIComponent(slim)}`
  } catch {
    return dataUrl
  } finally {
    document.body.removeChild(iframe)
  }
}
